import {
    deleteOrganizationLogo,
    getOrganizationLogo,
    IOrganization,
} from "@/api";
import { PageTitle } from "@/components/shared/layout/PageTitle";
import { Close } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Stack } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { LogoFileDrop } from "./LogoFileDrop";

interface IProps {
    organization: IOrganization;
}

export const OrganizationLogo = ({ organization }: IProps) => {
    const queryClient = useQueryClient();
    const [isHovered, setIsHovered] = useState(false);

    const { data: logoUrl, isPending } = useQuery({
        queryKey: ["organizationLogo", organization.id],
        queryFn: () => getOrganizationLogo(organization.id),
    });

    const deleteMutation = useMutation({
        mutationFn: () => deleteOrganizationLogo(organization.id),
        onSuccess: () => {
            enqueueSnackbar("Logo deleted successfully!", {
                variant: "success",
                persist: false,
            });
            // Invalidate the query to refetch and show upload component
            queryClient.invalidateQueries({
                queryKey: ["organizationLogo", organization.id],
            });
        },
        onError: (error: unknown) => {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : "Failed to delete logo";
            enqueueSnackbar(`Delete failed: ${errorMessage}`, {
                variant: "error",
                persist: false,
            });
        },
    });

    const handleDelete = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        deleteMutation.mutate();
    };

    if (isPending) {
        return (
            <Stack
                direction={"row"}
                gap={2}
                sx={{ px: 2 }}
                alignItems={"center"}
            >
                <Stack
                    sx={{ width: 80, height: 80 }}
                    alignItems={"center"}
                    justifyContent={"center"}
                >
                    <CircularProgress size={24} />
                </Stack>
                <PageTitle title={organization.name} />
            </Stack>
        );
    }

    // If no logo URL is found, show the file drop component
    if (!logoUrl || !logoUrl.signed_url) {
        return (
            <Box sx={{ px: 2 }}>
                <LogoFileDrop organization={organization} />
            </Box>
        );
    }

    return (
        <Stack direction={"row"} gap={2} sx={{ px: 2 }} alignItems={"center"}>
            <Box
                sx={{
                    position: "relative",
                    width: 80,
                    height: 80,
                    border: 1,
                    borderColor: (theme) => theme.palette.gray.light,
                    borderRadius: 2,
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <Box
                    component="img"
                    src={logoUrl.signed_url}
                    sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "opacity 0.2s ease",
                        p: 0.5,
                        opacity:
                            isHovered || deleteMutation.isPending ? 0.8 : 1,
                    }}
                />

                {isHovered && (
                    <IconButton
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                        sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            backgroundColor: "error.main",
                            color: "white",
                            width: 24,
                            height: 24,
                            boxShadow: 2,
                            "&:hover": {
                                backgroundColor: "error.dark",
                            },
                            "&:disabled": {
                                backgroundColor: "error.light",
                                color: "white",
                                opacity: 0.5,
                            },
                        }}
                    >
                        <Close sx={{ fontSize: 18 }} color="inherit" />
                    </IconButton>
                )}
            </Box>
            <PageTitle title={organization.name} />
        </Stack>
    );
};
